import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import UserAvatar from "../UserAvatar";
import { getNicheMeta } from "../niche";
import { creatorProfilePath } from "../../utils/featuredDisplay";
import NominateCreatorButton from "../credits/NominateCreatorButton";

export default function NominationLeaderboard({ rows = [] }) {
  const boardRef = useRef(null);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            board.querySelectorAll(".ft-nom-bar-fill").forEach((bar) => {
              bar.classList.add("ft-nom-bar-fill--animated");
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(board);
    return () => observer.disconnect();
  }, [rows]);

  const maxCount = rows[0]?.count || 1;

  return (
    <section className="ft-nomination-wrap">
      <div className="ft-nomination-dots" aria-hidden />
      <div className="ft-nomination-inner">
        <div className="ft-nomination-copy">
          <div className="ft-nomination-eyebrow">Community powered</div>
          <h2 className="ft-nomination-title">
            Nomination <em>leaderboard</em>
          </h2>
          <p className="ft-nomination-desc">
            Top nominated creators this month. More nominations mean a higher chance in the
            nightly weighted lottery — never a guarantee.
          </p>
          <div className="ft-nom-rules">
            <div className="ft-nom-rule">
              <span className="ft-nom-dot" />
              50 credits per nomination — spend them wisely
            </div>
            <div className="ft-nom-rule">
              <span className="ft-nom-dot" />
              Max 1 nomination per creator per day
            </div>
            <div className="ft-nom-rule">
              <span className="ft-nom-dot" />
              Featured creators locked out for 30 days
            </div>
          </div>
          <span className="ft-nom-hand">50 credits per nomination · max 1 per creator per day</span>
        </div>

        <div className="ft-nom-board-wrap">
          <div className="ft-nom-board-label">Most nominated this month</div>
          <div className="ft-nom-board" ref={boardRef}>
            {rows.length === 0 ? (
              <p className="ft-nom-empty">No nominations recorded yet.</p>
            ) : (
              rows.map((row) => {
                const creator = row.creator;
                const name = creator?.displayName || "Creator";
                const meta = getNicheMeta(creator?.niche);
                const to = creator ? creatorProfilePath(creator) : null;
                const pct = Math.max(8, Math.round((row.count / maxCount) * 100));

                if (to) {
                  return (
                    <div key={row.creatorId} className="ft-nom-row ft-nom-row--with-action">
                      <Link to={to} className="ft-nom-row-link">
                        <div className={`ft-nom-rank${row.rank <= 2 ? " ft-nom-rank--gold" : ""}`}>
                          {row.rank}
                        </div>
                        <UserAvatar
                          src={creator?.avatarUrl || creator?.avatar}
                          alt={name}
                          name={name}
                          className="ft-nom-av"
                          imgClassName="ft-nom-av-img"
                          initialsClassName="ft-nom-av-initials"
                        />
                        <div className="ft-nom-info">
                          <div className="ft-nom-name">{name}</div>
                          <div className="ft-nom-niche">{meta.label}</div>
                        </div>
                        <div className="ft-nom-right">
                          <div className="ft-nom-num">{row.count}</div>
                          <div className="ft-nom-bar-w">
                            <div
                              className="ft-nom-bar-fill"
                              style={{ "--bar-w": `${pct}%` }}
                            />
                          </div>
                        </div>
                      </Link>
                      <NominateCreatorButton
                        creatorId={row.creatorId}
                        creatorName={name}
                        variant="leaderboard"
                        className="ft-nom-action"
                      />
                    </div>
                  );
                }

                return (
                  <div key={row.creatorId} className="ft-nom-row ft-nom-row--with-action">
                    <div className="ft-nom-row-link">
                      <div className={`ft-nom-rank${row.rank <= 2 ? " ft-nom-rank--gold" : ""}`}>
                        {row.rank}
                      </div>
                      <UserAvatar
                        src={creator?.avatarUrl || creator?.avatar}
                        alt={name}
                        name={name}
                        className="ft-nom-av"
                        imgClassName="ft-nom-av-img"
                        initialsClassName="ft-nom-av-initials"
                      />
                      <div className="ft-nom-info">
                        <div className="ft-nom-name">{name}</div>
                        <div className="ft-nom-niche">{meta.label}</div>
                      </div>
                      <div className="ft-nom-right">
                        <div className="ft-nom-num">{row.count}</div>
                        <div className="ft-nom-bar-w">
                          <div
                            className="ft-nom-bar-fill"
                            style={{ "--bar-w": `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <NominateCreatorButton
                      creatorId={row.creatorId}
                      creatorName={name}
                      variant="leaderboard"
                      className="ft-nom-action"
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
